import apiClient from '@/services/api_client';

export interface UserGroup {
    id: number;
    name: string;
    tenant_id: string;
    created_at: string;
    updated_at: string;
    member_count: number;
    description?: string;
    azure_group_id?: string;
}

export interface UserValidationResponse {
    email: string;
    exists: boolean;
    user_id: string | null;
    name: string | null;
}

export interface GroupMember {
    user_id: string;
    name: string | null;
    email: string | null;
}

export interface GroupMembersResponse {
    group_id: number;
    members: GroupMember[];
}

interface CreateUserGroupParams {
    name: string;
    tenant_id: string;
    description?: string;
    azure_group_id?: string;
}

interface UpdateUserGroupParams {
    name?: string;
    description?: string;
}

export async function getUserGroups(tenantId: string): Promise<UserGroup[]> {
    const url = `/user_groups?tenant_id=${tenantId}`;
    const response = await apiClient.get(url);
    return await response.json();
}

export async function createUserGroup(
    params: CreateUserGroupParams
): Promise<UserGroup> {
    return await apiClient.post('/user_groups', params);
}

export async function updateUserGroup(
    groupId: number,
    tenantId: string,
    params: UpdateUserGroupParams
): Promise<UserGroup> {
    const url = `/user_groups/${groupId}?tenant_id=${tenantId}`;
    return await apiClient.patch(url, params);
}

export async function deleteUserGroup(
    groupId: number,
    tenantId: string
): Promise<void> {
    const url = `/user_groups/${groupId}?tenant_id=${tenantId}`;
    await apiClient.delete(url, {});
}

export async function getGroupMembers(
    groupId: number,
    tenantId: string
): Promise<GroupMembersResponse> {
    const url = `/user_groups/${groupId}/members?tenant_id=${tenantId}`;
    const response = await apiClient.get(url);
    return await response.json();
}

export async function addMemberToGroup(
    groupId: number,
    tenantId: string,
    email: string
): Promise<GroupMember> {
    const url = `/user_groups/${groupId}/members?tenant_id=${tenantId}`;
    return await apiClient.post(url, { email });
}

export async function removeMemberFromGroup(
    groupId: number,
    userId: string,
    tenantId: string
): Promise<void> {
    const url = `/user_groups/${groupId}/members/${userId}?tenant_id=${tenantId}`;
    await apiClient.delete(url, {});
}

export interface AzureGroup {
    id: string;
    displayName: string;
    description?: string;
    mail?: string;
}

export async function getAzureGroups(tenantId: string): Promise<AzureGroup[]> {
    const url = `/azure_groups?tenant_id=${tenantId}`;
    const response = await apiClient.get(url);
    return await response.json();
}

export interface AzureGroupMember {
    id: string;
    displayName?: string;
    mail?: string;
    userPrincipalName?: string;
}

export async function getAzureGroupMembers(
    groupId: string,
    tenantId: string
): Promise<AzureGroupMember[]> {
    const url = `/azure_groups/${groupId}/members?tenant_id=${tenantId}`;
    const response = await apiClient.get(url);
    return await response.json();
}

export interface ImportAllAzureGroupsResponse {
    imported: number;
    skipped: number;
    total: number;
}

export async function importAllAzureGroups(
    tenantId: string
): Promise<ImportAllAzureGroupsResponse> {
    const url = `/user_groups/import_all_azure_groups?tenant_id=${tenantId}`;
    return await apiClient.post(url, {});
}
